import type { HomeDto } from '@lmc/ts-common'
import {
  Button,
  FormItem,
  GroupedResidentsMultiSelect,
  RangePicker
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, Form, Modal } from 'antd'
import { showToast } from 'common/utils'
import dayjs, { type Dayjs } from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import {
  getReconciliationRangeDisabledDate,
  isValidReconciliationDateRange,
  RECONCILIATION_MAX_PAST_MONTHS
} from 'screens/Rostering/Reconciliation/reconciliationUtils'
import styled from 'styled-components'
import { StyledMultiSelect } from '../PayReport/payReportPageStyles'
import DownloadFileIcon from '../RotaOverview/components/DownloadFileIcon'
import { downloadChargeReportCsv } from './downloadChargeReportCsv'

type DownloadChargeReportModalProps = {
  isVisible: boolean
  onClose: () => void
  initialDateRange: [Dayjs, Dayjs]
  initialServiceUserIds: string[]
  initialFundingTypeIds: string[]
  serviceUserLabel: string
}

export const DownloadChargeReportModal = ({
  isVisible,
  onClose,
  initialDateRange,
  initialServiceUserIds,
  initialFundingTypeIds,
  serviceUserLabel
}: DownloadChargeReportModalProps) => {
  const { t } = useTranslation()
  const sendEvent = useMixpanelEvent()
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(initialDateRange)
  const [serviceUserIds, setServiceUserIds] = useState<string[]>(
    initialServiceUserIds
  )
  const [fundingTypeIds, setFundingTypeIds] = useState<string[]>(
    initialFundingTypeIds
  )
  const [isDownloading, setIsDownloading] = useState(false)

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  const { residents } = useResidentsList({})

  const fundingTypeOptions =
    home.rotaSettings?.fundingTypes
      ?.filter((fundingType) => fundingType.id && !fundingType.isDeleted)
      .map((fundingType) => ({
        label: fundingType.name,
        value: fundingType.id as string
      })) ?? []

  const initialStartMs = initialDateRange[0].valueOf()
  const initialEndMs = initialDateRange[1].valueOf()
  const initialServiceUsersKey = initialServiceUserIds.join(',')
  const initialFundingTypesKey = initialFundingTypeIds.join(',')

  // Preset the modal filters from the screen when it opens.
  useEffect(() => {
    if (!isVisible) {
      return
    }
    setDateRange([dayjs(initialStartMs), dayjs(initialEndMs)])
    setServiceUserIds(
      initialServiceUsersKey ? initialServiceUsersKey.split(',') : []
    )
    setFundingTypeIds(
      initialFundingTypesKey ? initialFundingTypesKey.split(',') : []
    )
  }, [
    isVisible,
    initialStartMs,
    initialEndMs,
    initialServiceUsersKey,
    initialFundingTypesKey
  ])

  const rangeValid = isValidReconciliationDateRange(dateRange[0], dateRange[1])

  const handleDownload = async () => {
    if (!rangeValid) {
      showToast({ title: t('PAY_REPORT_INVALID_RANGE'), type: 'error' })
      return
    }
    setIsDownloading(true)
    try {
      await downloadChargeReportCsv({
        startTime: dateRange[0].startOf('day').toISOString(),
        endTime: dateRange[1].endOf('day').toISOString(),
        reportType: 'charge_report',
        serviceUserLabel,
        ...(serviceUserIds.length ? { serviceUserIds } : {}),
        ...(fundingTypeIds.length ? { fundingTypeIds } : {})
      })
      sendEvent(MixpanelEvents.CHARGE_REPORT_CSV_DOWNLOADED, {
        numberOfDays: dateRange[1].diff(dateRange[0], 'day') + 1,
        filtersApplied: {
          serviceUsers: serviceUserIds.length > 0,
          fundingTypes: fundingTypeIds.length > 0
        }
      })
      onClose()
    } catch (error) {
      console.error('Failed to download CSV:', error)
      showToast({ title: t('SOMETHING_WENT_WRONG'), type: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      return
    }
    if (!isValidReconciliationDateRange(dates[0], dates[1])) {
      return
    }
    setDateRange([dates[0], dates[1]])
  }

  return (
    <Modal
      wrapProps={{ 'data-testid': 'charge-report-download-csv-modal' }}
      width={580}
      open={isVisible}
      onCancel={onClose}
      footer={
        <ModalFooter>
          <Button onClick={onClose} disabled={isDownloading}>
            {t('CANCEL')}
          </Button>
          <Button
            data-testid="charge-report-csv-modal-confirm-btn"
            onClick={handleDownload}
            variant="primary"
            disabled={!rangeValid}
            isLoading={isDownloading}
          >
            {t('PAY_REPORT_DOWNLOAD_CSV')}
            <DownloadFileIcon />
          </Button>
        </ModalFooter>
      }
      destroyOnClose
    >
      <ModalTitle>
        <Heading level={3}>{t('PAY_REPORT_DOWNLOAD_CSV')}</Heading>
        <StyledDivider />
      </ModalTitle>

      <Form layout="vertical" style={{ width: '100%' }}>
        <ModalContent>
          <FormItem
            label={<Text>{t('DATE_RANGE_MODAL')}</Text>}
            required
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            full
          >
            <FieldControl>
              <RangePicker
                value={dateRange}
                onChange={handleRangeChange}
                disabledDate={getReconciliationRangeDisabledDate}
                allowClear={false}
                format="DD-MM-YYYY"
                minDate={dayjs().subtract(
                  RECONCILIATION_MAX_PAST_MONTHS,
                  'month'
                )}
                maxDate={dayjs()}
                style={{ width: '100%' }}
              />
            </FieldControl>
          </FormItem>

          <FormItem
            label={<Text>{serviceUserLabel}</Text>}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            full
          >
            <FieldControl>
              <GroupedResidentsMultiSelect
                residents={residents}
                title={serviceUserLabel}
                values={serviceUserIds}
                handleInputChange={(selected) =>
                  setServiceUserIds(selected as string[])
                }
              />
            </FieldControl>
          </FormItem>

          <FormItem
            label={<Text>{t('CHARGE_REPORT_COL_FUNDING_TYPE')}</Text>}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            full
          >
            <FieldControl>
              <StyledMultiSelect
                title={t('CHARGE_REPORT_COL_FUNDING_TYPE')}
                options={fundingTypeOptions}
                values={fundingTypeIds}
                hasClear
                hasSearch
                hasSelectAll
                onChange={(selected) => setFundingTypeIds(selected as string[])}
              />
            </FieldControl>
          </FormItem>
        </ModalContent>
      </Form>
      <StyledDivider />
    </Modal>
  )
}

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ModalContent = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  .ant-form-item-label {
    padding-bottom: 4px;
  }
`

const FieldControl = styled.div`
  width: 400px;
  max-width: 100%;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`

const ModalFooter = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`
